import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { Button, Card, StepIndicator, SubtitleText, TitleText } from '@/components/ui'
import type { StepConfig, StepStatus } from '@/components/ui'
import { Layout } from '../layout/Layout'
import { useTheme } from '@/hooks/useTheme'
import { cn } from '@/utils/cn'

export interface AboutUsPageProps {
  className?: string
}

const ADHERENCE_SOURCE_URL = 'https://www.sciencedirect.com/science/article/pii/S2667276622000282'

const LOOP_STEPS: StepConfig[] = [
  { key: 'refer', title: 'Refer', icon: '1' },
  { key: 'plan', title: 'Plan', icon: '2' },
  { key: 'shop', title: 'Shop', icon: '3' },
  { key: 'review', title: 'Review', icon: '4' },
]

// The loop is an overview, not progress: every step reads as active.
const LOOP_STATUS: Partial<Record<string, StepStatus>> = Object.fromEntries(
  LOOP_STEPS.map((step) => [step.key, 'current'])
)

interface Step {
  lead: string
  body: string
}

const PATIENT_STEPS: Step[] = [
  {
    lead: 'Get referred.',
    body: 'Your GP, surgeon or specialist refers you to a dietitian through BiteWise. Some clinics let you refer yourself.',
  },
  {
    lead: 'Meet your dietitian.',
    body: "Book an appointment online. Your dietitian already has your referral and anything your doctor shared, so you won't have to start from scratch.",
  },
  {
    lead: 'Get a plan you can live with.',
    body: "Your dietitian builds a personalised meal plan with help from our AI. If a meal doesn't suit you, swap it for one that still fits the plan. Each plan comes with a shopping list you can take straight to the supermarket.",
  },
  {
    lead: 'Review and adjust.',
    body: 'Your dietitian follows up and updates the plan as things change. Your referring doctor can see where the referral is up to.',
  },
]

const DIETITIAN_STEPS: Step[] = [
  {
    lead: 'Receive referrals, not faxes.',
    body: "Referrals from GPs and specialists land in one place with the patient's history attached.",
  },
  {
    lead: 'Build plans faster.',
    body: 'Generate a draft meal plan in minutes, then apply your own clinical judgement. Every recommendation is yours to change.',
  },
  {
    lead: 'Give patients something usable.',
    body: 'Every plan comes with a shopping list, and patients can swap individual meals without breaking the plan.',
  },
  {
    lead: 'Keep the record straight.',
    body: 'Notes, documents and appointments stay with the referral, so follow-up is easy and nothing goes missing.',
  },
]

const BELIEFS: Step[] = [
  {
    lead: 'Nutrition is healthcare.',
    body: "If you're managing diabetes, heart disease or a gut condition, or recovering from surgery, what's on your plate is part of the treatment. It deserves the same care as a prescription.",
  },
  {
    lead: 'Dietitians lead, AI assists.',
    body: 'Our AI writes the draft. An Accredited Practising Dietitian decides what reaches the patient, and every plan on BiteWise is reviewed by one.',
  },
  {
    lead: 'Plans have to survive a weeknight.',
    body: 'We design for the kitchen, budget and supermarket you already have.',
  },
  {
    lead: 'Built for Australia.',
    body: 'Australian dietitians and Australian foods, with a referral flow that fits how clinics here already work.',
  },
]

const AUDIENCES: Step[] = [
  {
    lead: 'Doctors and clinics',
    body: 'GPs, surgeons and specialists who want nutrition care to be a one-click referral rather than a loose end.',
  },
  {
    lead: 'Accredited Practising Dietitians',
    body: 'Including those supporting bariatric and surgical patients, and people managing chronic conditions.',
  },
  {
    lead: 'Patients',
    body: 'Anyone whose doctor has said "you\'ll need to change how you eat" and wants help doing it.',
  },
]

const AboutUsPage: React.FC<AboutUsPageProps> = ({ className }) => {
  const { isDark } = useTheme()
  const navigate = useNavigate()

  useEffect(() => {
    document.title = 'About Us | BiteWise'
  }, [])

  const bodyClass = isDark ? 'text-secondary-300' : 'text-secondary-700'
  const strongClass = isDark ? 'text-white' : 'text-secondary-900'
  const mutedClass = isDark ? 'text-secondary-400' : 'text-secondary-300'
  const numberClass = isDark ? 'bg-primary-900 text-primary-300' : 'bg-primary-50 text-primary-600'
  const linkClass = cn(
    'hover:underline',
    isDark ? 'text-primary-400' : 'text-primary-500'
  )

  const goToPatientRegister = () => navigate('/patient/register')
  const goToDietitianRegister = () => navigate('/dietitian/register')

  const renderSteps = (heading: string, steps: Step[]) => (
    <div>
      <h4 className={cn('font-heading font-bold text-lg mb-3', strongClass)}>{heading}</h4>
      <ol className="space-y-4">
        {steps.map((step, index) => (
          <li key={step.lead} className="flex gap-3">
            <span
              aria-hidden="true"
              className={cn(
                'flex-none w-7 h-7 rounded-full text-sm font-bold flex items-center justify-center',
                numberClass
              )}
            >
              {index + 1}
            </span>
            <p>
              <strong className={cn('font-semibold', strongClass)}>{step.lead}</strong> {step.body}
            </p>
          </li>
        ))}
      </ol>
    </div>
  )

  return (
    <Layout className={className}>
      <div className="mx-auto max-w-4xl px-4 pt-12 pb-16">
        {/* Hero */}
        <header>
          <TitleText>Connecting clinical care with real-world nutrition.</TitleText>
          <p className={cn('text-lg', bodyClass)}>
            BiteWise links your doctor, an Accredited Practising Dietitian and a meal plan built around your life,
            so the advice you get in the clinic is something you can follow at home.
          </p>
        </header>

        {/* Why BiteWise exists */}
        <Card variant="bordered" shadow="soft" className="p-8 mt-10">
          <SubtitleText>Why BiteWise exists</SubtitleText>
          <div className={cn('space-y-4', bodyClass)}>
            <p>
              Most people leave an appointment with good dietary advice and no way to act on it. The advice sits on
              a printout while the shopping happens at 6pm on a Tuesday, and the two rarely meet.
            </p>
            <p>
              That isn't a willpower problem. It's the gap between clinical care and everyday life, and BiteWise
              exists to close it.
            </p>
            <p className={cn('text-xl font-semibold', strongClass)}>
              Fewer than half of patients follow the dietary advice they're given.<sup>1</sup>
            </p>
            <p className={cn('text-sm', mutedClass)}>
              <sup>1</sup> Adherence to dietary guidelines in chronic disease is below 50% despite clear clinical
              benefit, according to a systematic review of food-provision studies in heart disease, diabetes and
              kidney disease (
              <a href={ADHERENCE_SOURCE_URL} target="_blank" rel="noopener noreferrer" className={linkClass}>
                Food as medicine? Exploring the impact of providing healthy foods on adherence and clinical and
                economic outcomes, Am J Prev Cardiol, 2022
              </a>
              ).
            </p>
          </div>
        </Card>

        {/* How it works */}
        <Card variant="bordered" shadow="soft" className="p-8 mt-8">
          <SubtitleText>How it works</SubtitleText>
          <div className={bodyClass}>
            <div className="hidden sm:block">
              <StepIndicator
                steps={LOOP_STEPS}
                currentStep="refer"
                statusOverrides={LOOP_STATUS}
                size="md"
                ariaLabel="The BiteWise loop"
              />
            </div>
            <div className="sm:hidden">
              <StepIndicator
                steps={LOOP_STEPS}
                currentStep="refer"
                statusOverrides={LOOP_STATUS}
                size="sm"
                variant="vertical"
                ariaLabel="The BiteWise loop"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 mt-8">
              {renderSteps("If you're a patient", PATIENT_STEPS)}
              {renderSteps("If you're a dietitian", DIETITIAN_STEPS)}
            </div>
          </div>
        </Card>

        {/* What we believe */}
        <Card variant="bordered" shadow="soft" className="p-8 mt-8">
          <SubtitleText>What we believe</SubtitleText>
          <ul className={cn('space-y-4', bodyClass)}>
            {BELIEFS.map((belief) => (
              <li key={belief.lead}>
                <strong className={cn('font-semibold', strongClass)}>{belief.lead}</strong> {belief.body}
              </li>
            ))}
          </ul>
        </Card>

        {/* Who we work with */}
        <Card variant="bordered" shadow="soft" className="p-8 mt-8">
          <SubtitleText>Who we work with</SubtitleText>
          <ul className={cn('space-y-3', bodyClass)}>
            {AUDIENCES.map((audience) => (
              <li key={audience.lead}>
                <strong className={cn('font-semibold', strongClass)}>{audience.lead}.</strong> {audience.body}
              </li>
            ))}
          </ul>
        </Card>

        {/* Get started */}
        <Card variant="bordered" shadow="soft" className="p-8 mt-8">
          <SubtitleText>Get started</SubtitleText>
          <div className={cn('grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8', bodyClass)}>
            <div>
              <p>
                <strong className={cn('font-semibold', strongClass)}>Patients:</strong> ask your GP or specialist
                about a BiteWise referral, or follow the link your clinic gave you.
              </p>
              <div className="mt-4">
                <Button variant="accent" className="w-full sm:w-auto" onClick={goToPatientRegister}>
                  Book Appointment
                </Button>
              </div>
            </div>
            <div>
              <p>
                <strong className={cn('font-semibold', strongClass)}>Dietitians:</strong> apply to join the BiteWise
                network. Accreditation takes a few minutes to start.
              </p>
              <div className="mt-4">
                <Button variant="secondary" className="w-full sm:w-auto" onClick={goToDietitianRegister}>
                  Join Our Network
                </Button>
              </div>
            </div>
          </div>
          <p className={cn('mt-8', bodyClass)}>
            Questions?{' '}
            <a href="mailto:support@bitewise.com.au" className={linkClass}>
              support@bitewise.com.au
            </a>
          </p>
        </Card>
      </div>
    </Layout>
  )
}

export { AboutUsPage }
